import React, {useEffect, useState} from "react";
import {LucideCheck, LucideLock, LucideMessageCircleQuestion, LucideOctagonX} from "lucide-react";
import {useLocation, useNavigate, useSearchParams} from "react-router-dom";

export default function Toaster() {

    const location = useLocation()
    const navigate = useNavigate()
    const [search] = useSearchParams()

    const [state, setState] = useState<{location: string, msg: string | null, isError: boolean}|undefined>()

    useEffect(() => {
        if (state && state.location !== location.pathname){
            setState(undefined)
            return
        }

        const msg = search.get("msg")
        const err = search.get("err")

        if (msg || err){
            setState({msg: msg ?? err, isError: !msg && !!err, location: location.pathname})
            search.delete("msg")
            search.delete("err")
            navigate(`?${search.toString()}`, { replace: true });
        }

    }, [state, search, navigate, location.pathname])

    if (!state){
        return null
    }

    const [icon, message] = getIconAndMessage(state.msg)
    if (!icon || !message){
        return null
    }

    return <div className="py-2 sm:py-4 px-6 flex flex-col items-center justify-between mx-auto w-full max-w-4xl">
         <div className={`${state.isError ? "bg-red-500" : "bg-green-500"} rounded-md py-4 px-6 flex items-center w-full mx-6`}>
            {icon}
            <span className="ms-3 text-lg font-bold">{message}</span>
            <span className="flex-grow"></span>
            <button className="size-10 hover:bg-neutral-400/50 font-bold text-2xl text-center flex justify-center items-center rounded-md border border-white" onClick={() => setState(undefined)} >&times;</button>
        </div>
    </div>

}

function getIconAndMessage(code: string | null | undefined): [React.ReactElement | null, string] {

    // Subscriptions
    if (code === "sub_success"){
        return [<LucideCheck />, "Your subscription is now active"]
    }
    else if (code === "sub_canceled"){
        return [<LucideOctagonX />, "Your subscription has been cancelled"]
    }
    else if (code === "duplicate_subscription"){
        return [<LucideCheck />, "You already have an active subscription"]
    }
    else if (code === "no_sub"){
        return [<LucideOctagonX />, "You currently don't have an active subscription"]
    }

    // Login
    if (code === "logout_success") {
        return [<LucideCheck/>, "Logout successful"]
    } else if (code === "logout_error") {
        return [<LucideOctagonX />, "Logout failed"]
    }
    else if (code === "dc_invalid_redirect"){
        return [<LucideOctagonX />, "Invalid discord redirect url. Are you already signed in?"]
    }
    else if (code === "logged_in"){
        return [<LucideCheck />, "You are already logged in"]
    }

    // Api errors (DC)
    else if (code === "dc_token_response_invalid"){
        return [<LucideOctagonX />, "Invalid token returned by discord"]
    }
    else if (code === "dc_user_info_invalid"){
        return [<LucideOctagonX />, "Invalid user information returned by discord api"]
    }
    else if (code === "dc_email_invalid"){
        return [<LucideOctagonX />, "Your discord account needs a verified email address to register for RustServerTool"]
    }

    // Api errors (Steam)
    else if (code === "steam_requires_user"){
        return [<LucideOctagonX />, "Steam login requires an authenticated user"]
    }
    else if (code === "steam_openid_error"){
        return [<LucideOctagonX />, "Invalid OpenId response returned by steam"]
    }
    else if (code === "steam_openid_validate_fail"){
        return [<LucideOctagonX />, "Invalid steam login"]
    }
    else if (code === "steam_api_error"){
        return [<LucideOctagonX />, "Failed to get account information from steam api. Please try again later"]
    }

    // Other
    else if (code === "unauthorized") {
        return [<LucideLock />, "You need to be signed in to perform this action"]
    }
    else if (code){
        return [<LucideMessageCircleQuestion />, `Unknown error: ${code}`]
    }

    return [null,""]
}